import classnames from 'classnames';
import { uniqBy } from 'lodash';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import React from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';

import Loading from '../../generic/Loading';
import getDatasetFromProps from '../../utils/get-dataset-from-props';
import CreateDialog from '../CreateDialog';
import getMappings from '../mappings';
import { filterOption, getOptions } from '../utils';
import CheckboxOption from './CheckboxOption';

const CAMPAIGN = 'campaign';

const colorStyles = {
  option: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: data.color,
    };
  },
};

class ActiveView extends React.Component {
  state = { entities: [], selection: [], isLoading: true, fetchedOptions: [] };

  async componentDidMount() {
    const { type, value, isMulti, searchParams, name } = this.props;

    // name has been passed to distinguish between merchantName and merchantIds since it is different for Campaigns and SKU
    const { async, load } = getMappings(name)[type];
    const ids = isMulti ? value : [value];
    const hasSelection = value && ids.length;
    const entities = async ? [] : await load({ search: searchParams });
    let selectedEntities = [];

    if (hasSelection) {
      if (async) {
        selectedEntities = await load({
          search: { ...searchParams, id: ids },
        });
        this.setState({
          selection: getOptions(type, selectedEntities),
          isLoading: false,
        });
        return;
      }
      selectedEntities = ids
        .map(id => entities.find(entity => entity.id === id))
        .filter(_ => _);
    }

    this.setState({
      entities,
      selection: getOptions(type, selectedEntities),
      isLoading: false,
    });
  }

  handleFilter = (...all) => {
    const { type, minSearchLength } = this.props;
    return filterOption(type, ...all, minSearchLength);
  };

  handleAsyncLoad = (searchTerm, callback) => {
    const { type, minSearchLength } = this.props;
    const { load } = getMappings()[type];
    if (searchTerm.length < minSearchLength) {
      return Promise.resolve([]);
    }
    return load({ search: { searchTerm } }).then(data => {
      const { suggestions } = this.props;
      const suggestionValues = suggestions.map(suggestion => suggestion.value);
      const options = getOptions(type, data);
      const optionsWithSuggestions = options.map(option =>
        suggestionValues.includes(option.value)
          ? { ...option, color: '#d3f0dd' }
          : option,
      );
      this.setState({ fetchedOptions: optionsWithSuggestions }, () => {
        callback(optionsWithSuggestions);
      });
    });
  };

  handleAdd = entity => {
    const { type, isMulti } = this.props;
    const { entities, selection } = this.state;
    const newOption = getOptions(type, [entity])[0];
    const newEntities = sortBy(entities.concat(entity), 'id');
    const newSelection = isMulti ? selection.concat(newOption) : newOption;
    this.setState({ entities: newEntities }, () =>
      this.handleChange(newSelection),
    );
  };

  handleChange = selected => {
    const { isMulti } = this.props;
    if (isMulti) {
      this.setState({ selection: selected });
      const entities = map(selected, 'data');
      this.triggerChange(map(selected, 'value'), entities);
    } else {
      this.setState({ selection: [selected] });
      this.triggerChange(get(selected, 'value') || null, get(selected, 'data'));
    }
  };

  triggerChange(value, entities) {
    const { name, isMulti, onChange } = this.props;
    const syntheticSyntheticEvent = {
      target: {
        value,
        type: isMulti ? 'multiAssociation' : 'singleAssociation',
        name,
        dataset: getDatasetFromProps(this.props),
      },
    };

    onChange(syntheticSyntheticEvent, entities);
  }

  render() {
    const {
      id,
      onSelectBlur,
      readOnly,
      type,
      isMulti,
      additionalAddFormProps,
      hideAddButton,
      className,
      widthClassName,
      selectClassName,
      minSearchLength,
      suggestions,
      style,
      removeOption,
      ...rest
    } = this.props;
    const { selection, entities, isLoading, fetchedOptions } = this.state;
    const options =
      removeOption && type === CAMPAIGN
        ? getOptions(type, entities).filter(
            option => option.data.id !== removeOption,
          )
        : getOptions(type, entities);
    const uniqueOptionsWithSuggestions = uniqBy(
      [...suggestions, ...options],
      'value',
    );

    const defaultUniqueOptionsWithSuggestions = uniqBy(
      [...suggestions, ...fetchedOptions],
      'value',
    );

    if (isLoading) {
      return <Loading className="p-0" />;
    }
    const { add, async } = getMappings()[type];

    const SelectComponent = async ? AsyncSelect : Select;
    const commonProps = {
      placeholder: 'Type to filter',
      inputId: id,
      ...rest,
      isMulti,
      value: isMulti ? selection : selection[0],
      className: classnames('m-select', className),
      classNamePrefix: 'm-select',
      onBlur: onSelectBlur,
      isClearable: true,
      isDisabled: readOnly,
      onChange: this.handleChange,
    };
    const additionalProps = async
      ? {
          noOptionsMessage: ({ inputValue = '' }) =>
            inputValue.length < minSearchLength
              ? 'Type to load results'
              : `No ${type} found`,
          cacheOptions: true,
          // this is added from the state so that the async dropdown does not
          // close after selection of item since if this is not added then it leads
          // to the component being re rendered and hence the dropdown closing
          // For reference: https://github.com/JedWatson/react-select/issues/2260
          defaultOptions: defaultUniqueOptionsWithSuggestions,
          loadOptions: this.handleAsyncLoad,
        }
      : {
          noOptionsMessage: () => `No ${type} found`,
          filterOption: this.handleFilter,
          options: uniqueOptionsWithSuggestions,
        };

    const multiSelectProps = {
      closeMenuOnSelect: false,
      hideSelectedOptions: false,
      closeOnSelect: false,
      autoBlur: false,
    };

    return (
      <div
        className={classnames('d-flex align-items-center', widthClassName)}
        id={`${id}-container`}
        style={style}
      >
        <div className={classnames('flex-grow-1', selectClassName)}>
          {isMulti ? (
            <SelectComponent
              {...commonProps}
              {...additionalProps}
              {...multiSelectProps}
              components={{ Option: CheckboxOption }}
              styles={colorStyles}
            />
          ) : (
            <SelectComponent {...commonProps} {...additionalProps} />
          )}
        </div>
        {add && !hideAddButton && (
          <CreateDialog
            type={type}
            disabled={readOnly}
            onSuccess={this.handleAdd}
            addProps={additionalAddFormProps}
          />
        )}
      </div>
    );
  }
}

ActiveView.propTypes = {
  id: string, // so that this can be a label target
  onSelectBlur: func.isRequired,
  readOnly: bool,
  type: string.isRequired,
  value: oneOfType([number, arrayOf(number.isRequired), string]), // Single- and MultiAssociation respectively
  isMulti: bool.isRequired,
  onChange: func,
  name: string.isRequired,
  searchParams: object, // eslint-disable-line react/forbid-prop-types,
  additionalAddFormProps: object, // eslint-disable-line react/forbid-prop-types,
  hideAddButton: bool,
  className: string,
  widthClassName: string,
  minSearchLength: number,
  selectClassName: string,
  style: object, // eslint-disable-line react/forbid-prop-types,
  suggestions: arrayOf(
    shape({ label: string, value: number, data: object, color: string }), // eslint-disable-line react/forbid-prop-types,
  ),
  removeOption: number,
};

ActiveView.defaultProps = {
  id: undefined,
  readOnly: false,
  value: null,
  onChange: () => false,
  searchParams: undefined,
  additionalAddFormProps: undefined,
  hideAddButton: false,
  className: undefined,
  widthClassName: undefined,
  minSearchLength: 3,
  selectClassName: undefined,
  style: undefined,
  suggestions: [],
  removeOption: undefined,
};

export default ActiveView;
